import { getGetPrimaryPaymentMethodInPublicQueryKey } from '@modusign/openapi-client-payment';
import { GetPrimaryPaymentMethodResDtoInPublic } from '@modusign/openapi-client-payment/generated/model';
import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import moment from 'moment';

import { updateEmailSenderSuccess } from '@app/features/customBrandSetting/email';
import { startUpload } from '@app/features/externalDocument';
import { payInvoice } from '@app/features/payment';
import {
  activateSubscription,
  buyProduct,
  changeSubscription,
  createSubscription,
  createSubscriptionSuccess,
  pauseSubscription,
  resumeSubscription,
  startTrialPlanSuccess,
  startTrialWithSurveySuccess,
} from '@app/features/subscription/state';
import SubscriptionStatus from '@app/features/subscription/types/SubscriptionStatus';
import pushAdobeDataLayer from '@app/libs/analytics/adobe';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import workspaceService from '@app/libs/workspaceService';
import { queryClient } from '@app/queryClient';
import { signInSuccess, signUpSuccess } from '@app/store/account/local/actions';
import { signOutSuccess } from '@app/store/account/sign-out/actions';
import {
  thirdPartySignInSuccess,
  thirdPartySignUpSuccess,
} from '@app/store/account/third-party/actions';
import { contractProgressRejectionSuccess } from '@app/store/contract-progress/actions';
import { contractSetupSubmitted } from '@app/store/contract-setup/actions';
import { cancelSigningSuccess, rejectDocumentSuccess } from '@app/store/document/actions';
import { downloadDocumentFileSuccess } from '@app/store/document/file/actions';
import { cancelRequestSuccess } from '@app/store/documentProgress/actions';
import {
  cancelSigningSuccess as cancelSigningSuccessDocuments,
  rejectDocumentSuccess as rejectDocumentSuccessDocuments,
  reuseDocumentSuccess,
} from '@app/store/documents/actions';
import { documentsExportFetchListEnd } from '@app/store/documentsExport/actions';
import { editingDocumentSubmitSuccess } from '@app/store/editingDocument/actions';
import { RootState } from '@app/store/types';

const dataLayerMiddleware = createListenerMiddleware<RootState>();

// 가입 신청
dataLayerMiddleware.startListening({
  matcher: isAnyOf(signUpSuccess, thirdPartySignUpSuccess),
  effect: (action) => {
    const { provider } = action.payload ?? {};
    if (provider) {
      pushDataLayer({ event: 'requestSignUp', signUpType: provider });
      pushDataLayer({ event: 'authenticateSignUp', signUpType: provider });
    } else {
      pushDataLayer({ event: 'requestSignUp', signUpType: 'email' });
    }
  },
});

// 트라이얼 요금제 시작
dataLayerMiddleware.startListening({
  matcher: isAnyOf(startTrialWithSurveySuccess, startTrialPlanSuccess),
  effect: () => pushAdobeDataLayer({ event: 'getTrialPlan' }),
});

dataLayerMiddleware.startListening({
  actionCreator: startUpload,
  effect: ({ payload }) =>
    pushDataLayer({
      event: 'startUploadingExternalDocument',
      externalDocumentCount: payload.documents.length,
    }),
});

// 서명 요청
dataLayerMiddleware.startListening({
  actionCreator: contractSetupSubmitted,
  effect: (action) => {
    const { signerCount, documentId, labelIds, signingMethodTypes, fieldCounts, isInOrder } =
      action.payload;

    pushDataLayer({
      event: 'requestSigning',
      document: {
        id: documentId,
        participantCount: signerCount,
        labelIdsCount: labelIds?.length || 0,
        fieldCounts,
        signingMethodTypes,
        isInOrder,
      },
    });
    pushDataLayer({ document: undefined });
  },
});

// 문서 편집기 편집 완료
dataLayerMiddleware.startListening({
  actionCreator: editingDocumentSubmitSuccess,
  effect: () => pushAdobeDataLayer({ event: 'editingDocumentSubmit' }),
});

// 정기 결제 중단 예약
dataLayerMiddleware.startListening({
  matcher: isAnyOf(pauseSubscription.fulfilled),
  effect: (action) => {
    const subscription = action.payload;
    if (!subscription.plan) {
      return;
    }
    pushDataLayer({ event: 'reserveUnsubscription' });
  },
});

// 정기 결제 중단 예약 취소
dataLayerMiddleware.startListening({
  matcher: isAnyOf(resumeSubscription.fulfilled),
  effect: (action) => {
    const subscription = action.payload;
    if (!subscription.plan) {
      return;
    }
    pushDataLayer({ event: 'cancelUnsubscription' });
  },
});

// 이메일 로그인
dataLayerMiddleware.startListening({
  matcher: isAnyOf(signInSuccess),
  effect: () => pushDataLayer({ event: 'login', loginMethod: 'email' }),
});

// 소셜 로그인
dataLayerMiddleware.startListening({
  matcher: isAnyOf(thirdPartySignInSuccess),
  effect: () => pushDataLayer({ event: 'login', loginMethod: 'social' }),
});

// 이메일 맞춤 브랜딩 발신자 수정
dataLayerMiddleware.startListening({
  matcher: isAnyOf(updateEmailSenderSuccess),
  effect: () => pushDataLayer({ event: 'updateCustomEmailSender' }),
});

// 로그아웃
dataLayerMiddleware.startListening({
  matcher: isAnyOf(signOutSuccess),
  effect: () => pushDataLayer({ event: 'logout' }),
});

// 구독 생성
dataLayerMiddleware.startListening({
  matcher: isAnyOf(createSubscriptionSuccess, createSubscription.fulfilled),
  effect: (action) => {
    const subscription = action.payload;
    if (!subscription?.plan) {
      return;
    }

    const paymentMethod = queryClient.getQueryData<GetPrimaryPaymentMethodResDtoInPublic>(
      getGetPrimaryPaymentMethodInPublicQueryKey(workspaceService.getWorkspaceId())
    );

    pushAdobeDataLayer({
      event: 'createSubscription',
      product: {
        productName: subscription.plan.title,
        productType: '요금제',
        price: subscription.plan.price,
        quantity: 1,
      },
      response: {
        subscription,
      },
      payment: {
        paymentMethod,
      },
    });

    if (subscription.status === SubscriptionStatus.ACTIVE) {
      pushAdobeDataLayer({
        event: 'activateSubscription',
        product: {
          productName: subscription.plan.title,
          productType: '요금제',
          price: subscription.plan.price,
          quantity: 1,
        },
        response: {
          subscription,
        },
        payment: {
          paymentMethod,
        },
      });
    }
  },
});

// 구독 활성화
dataLayerMiddleware.startListening({
  actionCreator: activateSubscription.fulfilled,
  effect: (action) => {
    const subscription = action.payload;
    if (!subscription?.plan) {
      return;
    }

    const paymentMethod = queryClient.getQueryData<GetPrimaryPaymentMethodResDtoInPublic>(
      getGetPrimaryPaymentMethodInPublicQueryKey(workspaceService.getWorkspaceId())
    );

    pushAdobeDataLayer({
      event: 'activateSubscription',
      product: {
        productName: subscription.plan.title,
        productType: '요금제',
        price: subscription.plan.price,
        quantity: 1,
      },
      response: {
        subscription,
      },
      payment: {
        paymentMethod,
      },
    });
  },
});

// 구독 변경
dataLayerMiddleware.startListening({
  actionCreator: changeSubscription.fulfilled,
  effect: (action, { getOriginalState }) => {
    const subscription = action.payload;
    if (!subscription?.plan) {
      return;
    }

    const originalSubscription = getOriginalState().subscription.data;
    if (!originalSubscription.plan) {
      return;
    }

    const paymentMethod = queryClient.getQueryData<GetPrimaryPaymentMethodResDtoInPublic>(
      getGetPrimaryPaymentMethodInPublicQueryKey(workspaceService.getWorkspaceId())
    );

    // 요금제 id 가 변경되었을 때만 요금제 변경 이벤트를 보냅니다.
    if (originalSubscription.plan.id !== subscription.plan.id) {
      // 요금제 금액 산정(기본 요금 + 추가 인원 요금)
      const totalAmount =
        subscription.plan.price +
        subscription.plan.seatUnitPrice *
          ((subscription.seat?.quota || 0) - subscription.plan.defaultSeat);

      // 기존 요금제 환불 금액 산정(남은 기간 비율만큼 환불)
      const originalTotalAmount =
        originalSubscription.plan.price +
        originalSubscription.plan.seatUnitPrice *
          ((originalSubscription.seat?.quota || 0) - originalSubscription.plan.defaultSeat);
      const remainingPercentage =
        moment(originalSubscription.nextInvoicingDate).diff(moment(), 'days') /
        moment(originalSubscription.nextInvoicingDate).diff(
          moment(originalSubscription.updatedAt),
          'days'
        );

      pushAdobeDataLayer({
        event: 'changePlan',
        product: {
          productName: subscription.plan?.title,
          productType: '요금제 변경',
          price: totalAmount - originalTotalAmount * remainingPercentage,
          quantity: 1,
        },
        response: {
          subscription,
        },
        payment: {
          paymentMethod,
        },
      });
      return;
    }

    const originalQuota = originalSubscription.seat?.quota || 0;
    const quota = subscription.seat?.quota || 0;

    // 시트 수가 추가되었을 때만 시트 구매 이벤트를 보냅니다.
    if (originalQuota < quota) {
      pushAdobeDataLayer({
        event: 'buySeat',
        product: {
          productName: '시트',
          productType: '시트',
          price: subscription.plan.seatUnitPrice * (quota - originalQuota),
          quantity: quota - originalQuota,
        },
        response: {
          subscription,
        },
        payment: {
          paymentMethod,
        },
      });
    }
  },
});

// 사용량 구매
dataLayerMiddleware.startListening({
  actionCreator: buyProduct.fulfilled,
  effect: (action) => {
    const product = action.payload;
    const paymentMethod = queryClient.getQueryData<GetPrimaryPaymentMethodResDtoInPublic>(
      getGetPrimaryPaymentMethodInPublicQueryKey(workspaceService.getWorkspaceId())
    );

    pushAdobeDataLayer({
      event: 'buyProduct',
      product: {
        productName: product.name,
        productType: '사용량',
        price: product.price,
        quantity: product.quantity,
      },
      response: {
        product,
      },
      payment: {
        paymentMethod,
      },
    });
  },
});

// 청구서 지불
dataLayerMiddleware.startListening({
  actionCreator: payInvoice.fulfilled,
  effect: (action) => {
    const invoice = action.payload;
    const paymentMethod = queryClient.getQueryData<GetPrimaryPaymentMethodResDtoInPublic>(
      getGetPrimaryPaymentMethodInPublicQueryKey(workspaceService.getWorkspaceId())
    );

    pushAdobeDataLayer({
      event: 'payInvoice',
      product: {
        productName: '청구서',
        productType: '청구서',
        price: invoice.finalAmount,
        quantity: 1,
      },
      response: {
        invoice,
      },
      payment: {
        paymentMethod,
      },
    });
  },
});

// 서명 요청 취소
dataLayerMiddleware.startListening({
  actionCreator: cancelRequestSuccess,
  effect: () => {
    pushAdobeDataLayer({
      event: 'cancelSignRequest',
    });
  },
});

// 서명 거절
dataLayerMiddleware.startListening({
  matcher: isAnyOf(
    rejectDocumentSuccess,
    rejectDocumentSuccessDocuments,
    contractProgressRejectionSuccess
  ),
  effect: () => {
    pushAdobeDataLayer({
      event: 'rejectSignRequest',
    });
  },
});

// 서명 취소
dataLayerMiddleware.startListening({
  matcher: isAnyOf(cancelSigningSuccess, cancelSigningSuccessDocuments),
  effect: () => {
    pushAdobeDataLayer({
      event: 'cancelSign',
    });
  },
});

// 문서 복제
dataLayerMiddleware.startListening({
  actionCreator: reuseDocumentSuccess,
  effect: () => {
    pushAdobeDataLayer({
      event: 'reuseDocument',
    });
  },
});

// 문서 목록 내려받기 완료
dataLayerMiddleware.startListening({
  actionCreator: documentsExportFetchListEnd,
  effect: ({ payload }) => {
    pushDataLayer({
      event: 'exportDocumentsList',
      exportedDocumentCount: payload.totalDocumentsCount,
    });
  },
});

// 문서 다운로드
dataLayerMiddleware.startListening({
  actionCreator: downloadDocumentFileSuccess,
  effect: () => {
    pushDataLayer({
      event: 'downloadDocumentFile',
    });
  },
});

export default dataLayerMiddleware;
